import { FLEET_HUB_METHODS } from '../config/environment';
import { playFleetSound } from '../utils/fleetSound';
import { showFleetDesktopNotification, getIsFleetLeader } from './desktopNotify.service';
import { getFleetNotifyPrefs, categoryOfType } from '../utils/fleetNotifyPrefs';
import { routerOwnedType } from '../utils/fleetPayloadOwnership';
import { showFleetToast } from '../utils/toast';

/* Filo bildirim yönlendiricisi.

   SignalR olayları signalr.service.js tarafından `fleet:<HubMethod>` window olaylarına
   çevriliyor. Bu modül o olayları TEK yerde bildirim kararına dönüştürür; App.jsx yalnız
   veri tazeleme sorumluluğunu tutar.

   Kanal kuralları (partner panelindeki desenle aynı):
     • SES  → lider sekme VEYA görünür sekme (kullanıcı baktığı sekmede zili duymalı)
     • TOAST + MASAÜSTÜ → yalnız lider sekme (aksi halde çift bildirim) */

const TITLES = {
    UnassignedPackage: 'Atanmamış Paket',
    CourierNotFound: 'Kurye Bulunamadı',
    MaxReassign: 'Manuel Atama Gerekli',
    CourierRegionMissing: 'Bölge Ataması Eksik',
    /* DelayReminder BİLİNÇLİ OLARAK YOK. Yayıncının başlığı türe göre değişiyor
       (FleetDelayReminderJobService.BuildMessage): "Atanmamış Paket" / "Paket Bekliyor" /
       "Teslimat Gecikti". Buraya sabit bir başlık konursa, atanmamış paket hatırlatması
       "Teslimat Gecikti" başlığı altında "kurye bulunamadı, ... atanmadan bekliyor"
       gövdesini gösterir; üstelik aynı olay arka plan push'u olarak geldiğinde fleet-sw.js
       data.title'ı doğrudan okuduğu için DOĞRU başlıkla çıkar — tek olay, iki kanal, iki
       farklı başlık. Aşağıdaki `TITLES[type] || pick(d,'title','Title')` zinciri bu tipte
       yayıncının başlığına düşer. */
    CourierEmergency: 'Kurye Acil Durum',
    FleetJoinRequest: 'Yeni Katılma Talebi',
    FleetPaymentCreated: 'Yeni Hakediş',
    FleetPaymentApproved: 'Hakediş Onaylandı',
    FleetPaymentRejected: 'Hakediş Reddedildi',
    FleetPaymentPaid: 'Hakedişiniz Ödendi',
    FleetInvoiceApproved: 'Fatura Onaylandı',
    FleetInvoiceRejected: 'Fatura Reddedildi',
    CourierInvoiceUploaded: 'Kurye Fatura Yükledi',
    FleetCostUpdated: 'Teslimat Maliyetleri Güncellendi',

    VendorStoreAwaitingApproval: 'Mağaza Onayınızı Bekliyor',
    FleetIntegrationChannelChanged: 'Entegratör Kanalı Değişti',
};

/* Ses seçimi: acil durum ısrarlı üçlü, dispatch krizi kritik, red dikkat,
   onay/ödeme olumlu, gerisi genel. */
function soundOf(type) {
    if (type === 'CourierEmergency') return 'emergency';
    if (type === 'UnassignedPackage' || type === 'CourierNotFound'
        || type === 'MaxReassign' || type === 'CourierRegionMissing'
        || type === 'DelayReminder') return 'critical';
    /* Onay bekleyen mağaza kritik: onaylanana kadar o restorandan sipariş GELMİYOR,
       yani bekleyen bir bildirim değil duran bir ciro. Kanal değişimi bilgi. */
    if (type === 'VendorStoreAwaitingApproval') return 'critical';
    if (typeof type === 'string' && type.includes('Rejected')) return 'critical';
    if (typeof type === 'string' && (type.includes('Approved') || type.includes('Paid'))) return 'success';
    return 'info';
}

function isPageVisible() {
    return typeof document === 'undefined' || document.visibilityState === 'visible';
}

/* Toast görsel sözlüğü: kaldırılan handleJoinRequest / handleFleetNotification
   içindeki simge+renk eşlemesi birebir buraya taşındı — bkz. handleJoinRequest
   (👤 turuncu) ve handleFleetNotification (acil kırmızı, ödeme/fatura sarı, red
   amber, onay/ödendi yeşil, gerisi indigo). */
/* Sunucudan gelen başlıklar hâlâ emoji ile başlayabiliyor ("… Paket Bekliyor"); simge zaten
   toastIconOf'tan çizildiği için baştaki emoji ayıklanır — başlıkta iki ayrı simge durmasın. */
function plainTitle(value) {
    if (typeof value !== 'string') return value;
    return value.replace(/^[^\p{L}\p{N}]+/u, '').trim() || value;
}

function toastIconOf(type) {
    if (type === 'FleetJoinRequest') return 'user';
    if (type === 'CourierEmergency') return 'siren';
    if (type === 'CourierInvoiceUploaded') return 'file';
    if (type === 'VendorStoreAwaitingApproval') return 'store';
    if (type === 'FleetIntegrationChannelChanged') return 'plug';
    if (typeof type === 'string' && (type.includes('Payment') || type.includes('Invoice'))) return 'wallet';
    return 'bell';
}

function toastColorOf(type) {
    if (type === 'FleetJoinRequest') return '#f97316';
    if (type === 'CourierEmergency') return '#ef4444';
    /* Panelde onay kartı amber; toast da amber ki filo ikisini aynı iş olarak görsün. */
    if (type === 'VendorStoreAwaitingApproval') return '#f59e0b';
    if (type === 'FleetIntegrationChannelChanged') return '#3b82f6';
    if (typeof type === 'string' && type.includes('Rejected')) return '#f59e0b';
    if (typeof type === 'string' && (type.includes('Approved') || type.includes('Paid'))) return '#22c55e';
    return '#6366f1';
}

/* Acil durum bildirimi ekranda daha uzun kalır, gerisi standart 5 saniye. */
function toastAutoCloseOf(type) {
    return type === 'CourierEmergency' ? 15000 : 5000;
}

/* Backend gövdeleri hem camelCase hem PascalCase gelebiliyor (SignalR yayıncıları
   anonim nesne kullanıyor); iki biçimi de okuruz. */
function pick(d, ...keys) {
    for (const k of keys) {
        if (d[k] !== undefined && d[k] !== null && d[k] !== '') return d[k];
    }
    return null;
}

/* Gövde hiç metin taşımıyorsa bile bildirim boş çıkmasın: başlıksız/gövdesiz bir toast
   kullanıcıya "bir şey oldu ama ne?" dedirtir. Yayıncı tarafı title/body göndermeye
   geçtiği için buraya normalde düşülmez; bu son savunma hattıdır. */
const FALLBACK_BODY = 'Bildirim alındı.';

const HANDLED_METHODS = [
    FLEET_HUB_METHODS.RECEIVE_FLEET_NOTIFICATION,
    FLEET_HUB_METHODS.RECEIVE_FLEET_DISPATCH_NOTIFICATION,
    FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION,
    /* Gecikme hatırlatmaları (FleetDelayReminderJobService) bu kanaldan geliyor. Kanal aynı
       zamanda sıradan teslimat durum değişimlerini de taşıyor; onlar `type` alanı taşımadığı
       için aşağıdaki `if (!type) return` kapısında elenir ve App.jsx'teki mevcut işleyicide
       kalır. Tek-tüketici kuralı böyle korunur. */
    FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION,
];

export function startFleetNotificationRouter({ onNotification } = {}) {
    const handle = (e) => {
        const d = (e && e.detail) || {};

        /* Katılma talebi olayının kendi gövdesi var (type alanı taşımıyor). */
        const type = routerOwnedType(d)
            || (e.type.endsWith(FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION) ? 'FleetJoinRequest' : null);

        /* TEK TÜKETİCİ KURALI (bkz. utils/fleetPayloadOwnership.js): `type` taşımayan gövdeler
           App.jsx'in mevcut işleyicilerinde kalır — burada işlenirse aynı olay ikinci, başlıksız
           bir bildirime dönüşür. */
        if (!type) return;

        /* Başlıkta TITLES birincil: emoji + tutarlı Türkçe metin burada tutuluyor. Yayıncının
           gönderdiği title yalnız haritada olmayan (yeni eklenmiş) tipler için devreye girer. */
        const title = TITLES[type] || plainTitle(pick(d, 'title', 'Title')) || 'Filo Bildirimi';
        const body = pick(d, 'body', 'Body', 'message', 'Message') || FALLBACK_BODY;

        const prefs = getFleetNotifyPrefs();
        if (prefs[categoryOfType(type)] !== true) return;

        if (getIsFleetLeader() || isPageVisible()) {
            playFleetSound(soundOf(type));
        }

        if (!getIsFleetLeader()) return;

        /* Toast, tarayıcı bildirim izni olmasa/reddedilmiş olsa da görünür — masaüstü
           bildiriminin çizilip çizilmediğine bakılmaksızın koşulsuz gösterilir. */
        showFleetToast({
            icon: toastIconOf(type),
            color: toastColorOf(type),
            title,
            message: body,
            autoClose: toastAutoCloseOf(type),
        });

        showFleetDesktopNotification({ type, title, body });

        if (typeof onNotification === 'function') {
            onNotification({ type, title, body, raw: d });
        }
    };

    const listeners = HANDLED_METHODS.map((m) => {
        const name = `fleet:${m}`;
        window.addEventListener(name, handle);
        return name;
    });

    return () => listeners.forEach((name) => window.removeEventListener(name, handle));
}
